import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../components/ui/dialog_v2";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Badge } from "../../../components/ui/badge";
import { Pencil } from "lucide-react";
import { ROLE_CONFIG } from "./credit-data";
import { cn } from "lib/utils";
import localization from "services/localization";
import { useThemeStore } from "../../../context/useThemeStore";
import { toast } from "react-toastify";

export function EditAllocationModal({ open, onClose, user, remainingOrgCredits, onSave }) {
  const [newAllocation, setNewAllocation] = useState("");
  const [error, setError] = useState("");
  const { theme } = useThemeStore();

  useEffect(() => {
    if (user && open) {
      setNewAllocation(String(user.remaining || 0));
      setError("");
    }
  }, [user, open]);

  if (!user) return null;

  const diff = Number(newAllocation || 0) - user.remaining;
  const maxAllocation = user.remaining + remainingOrgCredits;

  const handleSave = () => {
    const numValue = Number(newAllocation);
    if (isNaN(numValue) || numValue < 0) {
      setError(localization.enter_valid_positive_number || "Please enter a valid positive number");
      return;
    }
    // if (numValue < user.used) {
    //   setError(
    //     (
    //       localization.allocation_cannot_be_less || "Allocation cannot be less than credits already used ({used})"
    //     ).replace("{used}", user.used.toLocaleString()),
    //   );
    //   return;
    // }
    if (numValue > maxAllocation) {
      setError(
        (localization.max_allocation_is || "Maximum allocation is {max} (current + {available} available)")
          .replace("{max}", maxAllocation.toLocaleString())
          .replace("{available}", remainingOrgCredits.toLocaleString()),
      );
      return;
    }
    if (numValue > remainingOrgCredits) {
      toast.error(localization.insufficient_credits || "Insufficient credits");
      setError(
        (localization.only_available_credits || "Only {credits} credits available").replace(
          "{credits}",
          remainingOrgCredits.toLocaleString(),
        ),
      );
      return;
    }
    onSave(user.wallet, user.id, numValue);
    onClose();
  };

  const handleClose = () => {
    setError("");
    setNewAllocation("");
    onClose();
  };

  const select_bg = theme === "dark" ? "bg-[var(--card-bg)] text-white" : "bg-white text-foreground";
  const select_bg2 = theme === "dark" ? " text-white" : "text-foreground";

  return (
    <Dialog open={open} onOpenChange={handleClose} className={select_bg}>
      <DialogContent className={"sm:max-w-md " + select_bg}>
        <DialogHeader>
          <DialogTitle className={"flex items-center gap-2 " + select_bg2}>
            <Pencil className="h-4 w-4 text-muted-foreground" />
            {localization.edit_credit_allocation || "Edit Credit Allocation"}
          </DialogTitle>
          <DialogDescription className={select_bg2}>
            {localization.adjust_credit_allocation || "Adjust the credit allocation for this user"}
          </DialogDescription>
          <DialogDescription className={select_bg2}>
            {localization.changes_affect_pool || "Changes affect the organization's available pool."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 py-2">
          {/* User Info */}
          <div className="flex items-center gap-3 rounded-lg border bg-muted/30 p-3">
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-bold text-primary-foreground"
              style={{
                backgroundColor: ROLE_CONFIG[user.role]?.color || "hsl(var(--primary))",
              }}
            >
              {user.name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium leading-none">{user.name}</p>
              <p className="mt-1 text-xs text-muted-foreground">{user.email}</p>
            </div>
            <Badge
              variant="outline"
              className={cn("font-medium", ROLE_CONFIG[user.role]?.bgClass, ROLE_CONFIG[user.role]?.textClass)}
            >
              {ROLE_CONFIG[user.role]?.label || user.role}
            </Badge>
          </div>

          {/* Current Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg border bg-muted/20 p-3 text-center">
              <p className="text-xs text-muted-foreground">{localization.total_allocation || "Total Allocation"}</p>
              <p className="mt-1 text-lg font-bold tabular-nums">{user.allocated.toLocaleString()}</p>
            </div>
            <div className="rounded-lg border bg-muted/20 p-3 text-center">
              <p className="text-xs text-muted-foreground">{localization.used || "Used"}</p>
              <p className="mt-1 text-lg font-bold tabular-nums text-amber-600">{user.used.toLocaleString()}</p>
            </div>
            <div className="rounded-lg border bg-muted/20 p-3 text-center">
              <p className="text-xs text-muted-foreground">{localization.remaining || "Remaining"}</p>
              <p className="mt-1 text-lg font-bold tabular-nums text-emerald-600">{user.remaining.toLocaleString()}</p>
            </div>
          </div>

          {/* New Allocation Input */}
          <div className="grid gap-2">
            <Label htmlFor="new-allocation">{localization.new_remaining || "New Remaining"}</Label>
            <Input
              className={select_bg}
              id="new-allocation"
              type="number"
              // min={user.used}
              min={0}
              max={maxAllocation}
              value={newAllocation}
              onChange={(e) => {
                setNewAllocation(e.target.value);
                setError("");
              }}
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {localization.min_used || "Min"}: {"0".toLocaleString()}
              </span>
              <span>
                {localization.max || "Max"}: {maxAllocation.toLocaleString()}
              </span>
            </div>
          </div>

          {/* Diff Preview */}
          {diff !== 0 && !error && (
            <div
              className={cn(
                "flex items-center justify-between rounded-lg border p-3",
                diff > 0
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                  : "border-amber-200 bg-amber-50 text-amber-800",
              )}
            >
              <span className="text-sm font-medium">
                {diff > 0 ? localization.adding || "Adding" : localization.reclaiming || "Reclaiming"}{" "}
                {localization.credits || "credits"}
              </span>
              <span className="text-sm font-bold tabular-nums">
                {diff > 0 ? "+" : ""}
                {diff.toLocaleString()} {localization.credits || "credits"}
              </span>
            </div>
          )}

          {error && <p className="text-sm font-medium text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {localization.cancel || "Cancel"}
          </Button>
          <Button onClick={handleSave}>{localization.save_changes || "Save Changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
